import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Phone, Lock, Target } from 'lucide-react';
import PhaseIntroCard from '@/features/dashboard/PhaseIntroCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import CCCWidget from '@/features/crystal-clear/CCCWidget';

const Phase3 = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const step1Done = user.clarityCallBooked;
  const step2Done = user.criteriaSubmitted === true;
  const canAdvance = step1Done && step2Done;

  return (
    <div>
      <PhaseIntroCard
        phase={3}
        title="Clarity"
        icon={Target}
        description="Shiny object syndrome and indecision are what keep most people from ever taking action. This phase ends that. You'll get crystal clear on the exact deal you're going to buy so you always know what's a real opportunity and what's a distraction. Focus is a skill. This is where you build it."
      />

      <div className="space-y-4 mb-8">

        {/* Step 1: Book a clarity call */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          className={`p-5 rounded-lg border bg-card shadow-sm ${step1Done ? 'border-gold/30 bg-gold/5' : 'border-border'}`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-0.5">
              {step1Done ? (
                <CheckCircle2 className="h-6 w-6 text-gold" />
              ) : (
                <div className="w-6 h-6 rounded-full border-2 border-gold flex items-center justify-center">
                  <span className="text-xs font-bold text-gold">1</span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-serif font-bold text-foreground text-lg mb-1">Book a clarity call with a mentor</h3>
              {step1Done ? (
                <p className="text-sm text-gold font-medium">Completed</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-5">
                    Get clear on what you're looking for. Choose the option that works best for you:
                  </p>

                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-lg border-2 border-gold/40 bg-gold/5">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="h-12 w-12 rounded-lg gradient-gold flex items-center justify-center shrink-0">
                          <Phone className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground">Book a clarity call with a mentor</p>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            Talk through your criteria 1-on-1 with a First Deal mentor who can guide you.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/clarity-call')}
                        className="flex items-center justify-center gap-1.5 px-4 py-2.5 gradient-gold text-primary-foreground text-sm font-semibold rounded-lg shadow-gold hover:opacity-90 transition-[transform,opacity] duration-150 active:scale-[0.97] sm:whitespace-nowrap sm:shrink-0 cursor-pointer"
                      >
                        Book Call <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Step 2: Submit Crystal Clear Criteria */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          className={`p-5 rounded-lg border bg-card shadow-sm ${
            step2Done ? 'border-gold/30 bg-gold/5' : 'border-border'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-0.5">
              {step2Done ? (
                <CheckCircle2 className="h-6 w-6 text-gold" />
              ) : (
                <div className="w-6 h-6 rounded-full border-2 border-gold flex items-center justify-center">
                  <span className="text-xs font-bold text-gold">2</span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-serif font-bold text-foreground text-lg mb-1">Submit Your Crystal Clear Criteria</h3>
              {step2Done ? (
                <p className="text-sm text-gold font-medium">Completed</p>
              ) : (
                <p className="text-sm text-muted-foreground mb-4">
                  Fill in each section of your Crystal Clear Criteria below. Once all six fields are saved, this step is complete.
                </p>
              )}
              <div className="mt-3">
                <CCCWidget />
              </div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Bottom CTA */}
      {canAdvance ? (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => updateUser({ phase: 4 })}
          className="flex items-center gap-2 px-6 py-3 gradient-gold text-primary-foreground font-semibold rounded-md shadow-gold hover:opacity-90 transition-[transform,opacity] duration-150 active:scale-[0.97]"
        >
          Move to Phase 4 -- Action <ArrowRight className="h-4 w-4" />
        </motion.button>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <button
            disabled
            className="flex items-center gap-2 px-6 py-3 bg-muted text-muted-foreground font-semibold rounded-md border border-border cursor-not-allowed opacity-60"
          >
            <Lock className="h-4 w-4" />
            I've defined my investing criteria
          </button>
          <p className="text-xs text-muted-foreground">
            Complete both steps above to unlock: book your clarity call and submit your Crystal Clear Criteria.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default Phase3;
